import { mainApi } from '../../../../api/mainApi';

import { middlewareApi } from '../../../middleware/middlewareApi';

import { AccessState } from '../../../../types';
import { HolidayPayload, HolidaysResponse } from '../interfaces/holidays.interfaces';
import {
  DayType,
  AcademicPlanningPayload,
  Planning,
} from '../interfaces/posgraduateDaysTypes.interfaces';
import { AcademicPlanningPosgraduateState, setColorEvents } from './acPlanPostgraduateSlice';
import { TemporaryPlanningPayload } from '../interfaces/temporaryPlanningPayload.interfaces';
import {
  PosgraduateSubject,
  TemporaryPlanningResponse,
} from '../interfaces/temporaryPlanningResponse.interfaces';
import {
  InfoPlanningResponse,
  Parallel,
  ProgramPlanningPayload,
} from '../interfaces/programPlanningPayload.interfaces';
import { Hourhand } from '../../hourhands/interfaces/hourhands.interfaces';
import { Collaborator } from '../../courses/interfaces/course.interface';
import { Classroom } from '../../classrooms/interfaces/classrooms.interface';
import { PostgraduatePlanificationResponse } from '../interfaces/postgraduatePlanification.interface';
import { ProgramPlanning } from '../interfaces/programPlanningResponse.interface';
import { StagePayload } from '../interfaces/period-form.interfaces';
import { IResponse } from '../../../shared/interfaces/response.interface';

export const acPlanPosgraduateTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['AcademicPlanningPostgraduate', 'Holidays'],
});
// Define a service using a base URL and expected endpoints
export const acPlanPosgraduateApi = acPlanPosgraduateTags.injectEndpoints({
  // overrideExisting: false,

  endpoints: (builder) => ({
    /**
     * @POST Create programs plannings
     */
    createProgramPlanning: builder.mutation<
      any,
      { academicPlanningPayload: Partial<ProgramPlanningPayload> }
    >({
      queryFn: async ({ academicPlanningPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<ProgramPlanningPayload>(
            `/postgraduates/planings/programs`,
            {
              user_profile_id,
              ...academicPlanningPayload,
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error) => [
        { type: 'AcademicPlanningPostgraduate', id: 'TEMPORARY' },
        { type: 'AcademicPlanningPostgraduate', id: 'TABLE' },
      ],
    }),

    /**
     * @GET all holidays availables
     */
    getHolidaysAvailables: builder.query<
      HolidaysResponse,
      {
        profileId?: number;
        currentCountry: string;
        yearPlusOne?: boolean;
        year?: number;
        generateHoliday?: boolean;
        calendarId?: string;
      }
    >({
      queryFn: async (
        { profileId, currentCountry, yearPlusOne, year, generateHoliday, calendarId },
        { getState, dispatch }
      ) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;
        try {
          const { data } = await mainApi.get<HolidaysResponse>(`/holidays`, {
            params: {
              user_profile_id,
              current_country: currentCountry,
              program_initial: 'true',
              ...(year && { year }),
              ...(yearPlusOne && { year_plus_one: yearPlusOne }),
              ...(generateHoliday && { generate_holiday: generateHoliday }),
              ...(calendarId && { planning_calendar_id: calendarId }),

              // data: 'all',
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'Holidays', id: 'LIST' }],
    }),

    /**
     * @GET all colors availables
     */
    getDaysTypesAvailables: builder.query<DayType[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState, dispatch }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<DayType[]>(`/postgraduate-day-types`, {
            params: {
              user_profile_id,
              data: 'all',
            },
          });

          dispatch(setColorEvents(Array.isArray(data) ? data : []));

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'Holidays', id: 'LIST_EVENT_TYPES' }],
    }),

    /**
     * @POST add academic planning
     */
    addDatesPlanning: builder.mutation<
      any,
      { academicPlanningPayload: Partial<AcademicPlanningPayload> }
    >({
      queryFn: async ({ academicPlanningPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>(`/areas`, {
            user_profile_id,
            ...academicPlanningPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error) => [{ type: 'AcademicPlanningPostgraduate', id: 'LIST' }],
    }),

    /**
     * @POST add temporary planning
     */
    addPlanningTemporary: builder.mutation<
      any,
      { academicPlanningPayload: Partial<TemporaryPlanningPayload> }
    >({
      queryFn: async ({ academicPlanningPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<TemporaryPlanningPayload>(
            `/postgraduate-temporary-planning`,
            {
              user_profile_id,
              ...academicPlanningPayload,
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error) => [
        { type: 'AcademicPlanningPostgraduate', id: 'TEMPORARY' },
      ],
    }),

    /**
     * @PUT update planning
     */
    editPlanningTemporary: builder.mutation<
      any,
      { academicPlanningPayload: Partial<TemporaryPlanningPayload> }
    >({
      queryFn: async ({ academicPlanningPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
            academicPlanningPosgraduate: { holidaysMove },
          } = getState() as {
            access: AccessState;
            academicPlanningPosgraduate: AcademicPlanningPosgraduateState;
          };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<TemporaryPlanningPayload>(
            `/postgraduate-temporary-planning`,
            {
              user_profile_id,
              ...academicPlanningPayload,
              ...(academicPlanningPayload?.calendarId && {
                planning_calendar_id: academicPlanningPayload?.calendarId,
              }),
              hourhands_move: holidaysMove,
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error) => [
        { type: 'AcademicPlanningPostgraduate', id: 'TEMPORARY' },
      ],
    }),

    addOrEditPlanning: builder.mutation<
      any,
      { datePayload: Partial<Planning>; calendarId: string }
    >({
      queryFn: async ({ datePayload, calendarId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>(`/postgraduate-temporary-planning/save`, {
            user_profile_id,
            ...datePayload,
            planning_calendar_id: calendarId,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error) => [
        { type: 'AcademicPlanningPostgraduate', id: 'TEMPORARY' },
      ],
    }),

    /**
     * @GET planning temporary
     */
    getPlanningTemporary: builder.query<
      TemporaryPlanningResponse,
      {
        profileId?: number;
        page: number;
        size: number;
        sort: string;
        typeSort: string;
        year: number;
        month: number;
        generate_holiday?: boolean;
        year_plus_one?: boolean;
        calendarId?: string;
      }
    >({
      queryFn: async (
        { profileId, page, size, sort, typeSort, year, month, calendarId },
        { getState }
      ) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<TemporaryPlanningResponse>(
            `/postgraduate-temporary-planning`,
            {
              params: {
                user_profile_id,
                page,
                size,
                sort,
                type_sort: typeSort,
                year,
                month,
                ...(calendarId && { planning_calendar_id: calendarId }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'AcademicPlanningPostgraduate', id: 'TEMPORARY' }],
    }),

    //
    getInfoPlanning: builder.query<
      InfoPlanningResponse,
      {
        profileId?: number;
        year: number;
        curriculumId: number;
      }
    >({
      queryFn: async ({ profileId, year, curriculumId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<InfoPlanningResponse>(`postgraduates/generate/name`, {
            params: {
              user_profile_id: user_profile_id,
              curriculum_id: curriculumId,
              off_description: 'oferta_postgrado',
              per_current_year: year,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),
    getCurriculumHourhands: builder.query<
      Hourhand[],
      {
        profileId?: number;
        curriculumId: number;
      }
    >({
      queryFn: async ({ profileId, curriculumId }, { getState }) => {
        try {
          const { data } = await mainApi.get<Hourhand[]>(
            `/postgraduates/curriculums/${curriculumId}/hourhands`,
            {
              params: {
                user_profile_id: profileId,
                curriculum_id: curriculumId,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),
    getPosgraduateCollaboratorsByCampus: builder.query<
      Collaborator[],
      {
        profileId?: number;
        campusId: number;
      }
    >({
      queryFn: async ({ profileId, campusId }, { getState }) => {
        try {
          const { data } = await mainApi.get<Collaborator[]>(`/postgraduates/collaborators`, {
            params: {
              user_profile_id: profileId,
              campus_id: campusId,
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getPosgraduateSubjectsByCurriculum: builder.query<
      PosgraduateSubject[],
      {
        profileId?: number;
        curriculumId: number;
      }
    >({
      queryFn: async ({ profileId, curriculumId }, { getState }) => {
        try {
          const { data } = await mainApi.get<PosgraduateSubject[]>(
            `/curriculums/${curriculumId}/subjects-posgrade`,
            {
              params: {
                user_profile_id: profileId,
                curriculum_id: curriculumId,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),
    getPosgraduateClassroomsByCampus: builder.query<
      Classroom[],
      {
        profileId?: number;
        campusId: number;
        hourhandId: number;
        startDate: string;
        endDate: string;
        method?: string;
        idTransactionPlanning?: string;
      }
    >({
      queryFn: async (
        { profileId, campusId, hourhandId, startDate, endDate, method, idTransactionPlanning },
        { getState }
      ) => {
        try {
          const { data } = await mainApi.get<Classroom[]>(
            `postgraduates/campus/${campusId}/classrooms`,
            {
              params: {
                user_profile_id: profileId,
                hourhand_id: hourhandId,
                start_process: startDate,
                end_process: endDate,
                campus_id: campusId,
                ...(method && { method }),
                ...(idTransactionPlanning && { id_transaction_planning: idTransactionPlanning }),
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    deleteTemporaryPlanning: builder.mutation<any, { id: string }>({
      queryFn: async ({ id }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.delete<any>(
            `/postgraduate-temporary-planning/completely/${id}`,
            {
              params: {
                user_profile_id,
              },
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },

      invalidatesTags: [
        {
          type: 'AcademicPlanningPostgraduate',
          id: 'TEMPORARY',
        },
      ],
    }),

    getPosgraduatePlanificationsList: builder.query<
      PostgraduatePlanificationResponse,
      {
        profileId?: number;
        page: number;
        size: number;
        sort: string;
        typeSort: string;
        search: string;
        campusId?: number;
        modalityId?: number;
        status?: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          size,
          sort,
          typeSort,
          search,
          campusId,
          status,
          startDate,
          endDate,
          modalityId,
        },
        { getState }
      ) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<PostgraduatePlanificationResponse>(
            `/postgraduates/postgraduate-planning-temp-list`,
            {
              params: {
                user_profile_id,
                page,
                size,
                sort,
                type_sort: typeSort,
                search,
                ...(campusId && { campus_id: campusId }),
                ...(modalityId && { modality_id: modalityId }),
                ...(status && { status_planning: status }),
                ...(startDate && { start_date: startDate }),
                ...(endDate && { end_date: endDate }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'AcademicPlanningPostgraduate', id: 'TABLE' }],
    }),
    approvePlanning: builder.mutation<any, { id: string; periodStageDates: StagePayload[][] }>({
      queryFn: async ({ id, periodStageDates }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<any>(
            `/postgraduates/planings/programs/change-status`,
            {
              user_profile_id,
              id_transaction_planning: id,
              period_stage_date: periodStageDates,
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'AcademicPlanningPostgraduate', id: 'TABLE' }],
    }),

    getPosgraduatePlanificationByTransactionId: builder.query<
      ProgramPlanning,
      {
        profileId?: number;
        id: string;
      }
    >({
      queryFn: async ({ profileId, id }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<ProgramPlanning>(`/postgraduates/planings-programs`, {
            params: {
              user_profile_id,
              id_transaction_planning: id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'AcademicPlanningPostgraduate', id: 'PLANNING' }],
    }),
    deletePostgraduateProgram: builder.mutation<any, { id: string }>({
      queryFn: async ({ id }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.delete<any>(
            `/postgraduate-temporary-planning/delete/program-start-point/${id}`,
            {
              params: {
                user_profile_id,
              },
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'AcademicPlanningPostgraduate', id: 'TABLE' },
        {
          type: 'AcademicPlanningPostgraduate',
          id: 'TEMPORARY',
        },
      ],
    }),

    addHoliday: builder.mutation<any, HolidayPayload>({
      queryFn: async (payload, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>(`/holidays`, {
            ...payload,
            ...(payload?.calendarId && { planning_calendar_id: payload.calendarId }),
            user_profile_id,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        {
          type: 'AcademicPlanningPostgraduate',
          id: 'TEMPORARY',
        },
        {
          type: 'Holidays',
          id: 'LIST',
        },
      ],
    }),

    updateHoliday: builder.mutation<
      any,
      {
        id: string;
        payload: Partial<HolidayPayload>;
        calendarId?: string;
      }
    >({
      queryFn: async ({ id, payload, calendarId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<any>(`/holidays/${id}`, {
            ...payload,
            user_profile_id,
            planning_calendar_id: calendarId,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },

      invalidatesTags: [
        {
          type: 'AcademicPlanningPostgraduate',
          id: 'TEMPORARY',
        },
        {
          type: 'Holidays',
          id: 'LIST',
        },
      ],
    }),

    deleteHoliday: builder.mutation<any, { id: string }>({
      queryFn: async ({ id }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.delete<any>(`/holidays/${id}`, {
            params: {
              user_profile_id,
            },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        {
          type: 'AcademicPlanningPostgraduate',
          id: 'TEMPORARY',
        },
        {
          type: 'Holidays',
          id: 'LIST',
        },
      ],
    }),

    getPosgraduateParallels: builder.query<
      IResponse<Parallel>,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search?: string;
        curriculumId?: number;
        year: number;
      }
    >({
      queryFn: async ({ profileId, page, perPage, search, curriculumId, year }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<IResponse<Parallel>>(`postgraduates/parallels`, {
            params: {
              user_profile_id,
              page,
              size: perPage,
              type_sort: 'asc',
              search,
              curriculum_id: curriculumId,
              off_description: 'oferta_postgrado',
              per_current_year: year,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'AcademicPlanningPostgraduate', id: 'PARALLELS' }],
    }),

    renameProgram: builder.mutation<any, { id: string; name: string }>({
      queryFn: async ({ id, name }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<any>(
            `/postgraduates/postgraduate-planning-additional-information/rename-period`,
            {
              postgraduate_planning_additional_information_id: id,
              rename_period: name,
              user_profile_id,
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        {
          type: 'AcademicPlanningPostgraduate',
          id: 'LIST',
        },
        {
          type: 'AcademicPlanningPostgraduate',
          id: 'TEMPORARY',
        },
        {
          type: 'AcademicPlanningPostgraduate',
          id: 'TABLE',
        },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetHolidaysAvailablesQuery,
  useLazyGetHolidaysAvailablesQuery,
  useGetDaysTypesAvailablesQuery,
  useLazyGetDaysTypesAvailablesQuery,
  useAddDatesPlanningMutation,
  useAddPlanningTemporaryMutation,
  useGetPlanningTemporaryQuery,
  useEditPlanningTemporaryMutation,
  useCreateProgramPlanningMutation,
  useLazyGetInfoPlanningQuery,
  useGetCurriculumHourhandsQuery,
  useGetPosgraduateCollaboratorsByCampusQuery,
  useGetPosgraduateSubjectsByCurriculumQuery,
  useGetPosgraduateClassroomsByCampusQuery,
  useAddOrEditPlanningMutation,
  useDeleteTemporaryPlanningMutation,
  useGetPosgraduatePlanificationsListQuery,
  useApprovePlanningMutation,
  useGetPosgraduatePlanificationByTransactionIdQuery,
  useDeletePostgraduateProgramMutation,
  useAddHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  useLazyGetPosgraduateParallelsQuery,
  useRenameProgramMutation,
} = acPlanPosgraduateApi;
